<script lang="ts">
import { computed, defineComponent, h, Transition, unref, watch } from 'vue';

import { classNames, convertToUnit } from '@components/utils';

import {
  makeTagProps,
  useProxyModel,
  useIntersectionObserver,
  useRender,
  makeThemeProps,
  provideTheme
} from '../../composable';
import { makeLocationProps, useLocation } from '../CdsOverlay/composable';

import type { PropType } from 'vue';

export const componentName = 'CdsProgress';
export default defineComponent({
  name: componentName,
  props: {
    modelValue: {
      type: [Number, String] as PropType<number | string>,
      default: 0
    },
    absolute: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    active: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    bgColor: {
      type: String as PropType<string>,
      default: undefined
    },
    bgOpacity: {
      type: [Number, String] as PropType<number | string>,
      default: undefined
    },
    bufferValue: {
      type: [Number, String] as PropType<number | string>,
      default: 0
    },
    height: {
      type: [Number, String] as PropType<number | string>,
      default: 4
    },
    indeterminate: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    clickable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    max: {
      type: [Number, String] as PropType<number | string>,
      default: 100
    },
    stream: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    reverse: {
      type: Boolean as PropType<boolean>,
      default: false
    },

    ...makeTagProps(),
    ...makeThemeProps(),
    ...makeLocationProps({
      location: 'top' as const
    })
  },
  emits: {
    'update:modelValue': (value: number) => true
  },
  setup(props, { slots, emit }) {
    const progress = useProxyModel(props, 'modelValue');
    const { themeClasses } = provideTheme(props);
    const { locationStyles } = useLocation(props);
    const { intersectionRef, isIntersection } = useIntersectionObserver();

    const max = computed(() => parseInt(props.max as string, 10));
    const height = computed(() => parseInt(props.height as string, 10));
    const normalizedBuffer = computed(() => (parseFloat(props.bufferValue as string) / unref(max)) * 100);
    const normalizedValue = computed(() => (parseFloat(unref(progress) as string) / unref(max)) * 100);
    const roundedValue = computed(() => Math.round(unref(normalizedValue)));
    const transition = computed(() => (props.indeterminate ? 'fade-transition' : 'slide-transition-x'));
    const opacity = computed(() => (props.bgOpacity == null ? props.bgOpacity : parseFloat(props.bgOpacity as string)));

    watch(progress, () => {
      emit('update:modelValue', unref(roundedValue));
    });

    function handleClick(event: MouseEvent) {
      if (!unref(intersectionRef)) return;

      const { left, right, width } = unref(intersectionRef)!.getBoundingClientRect();
      const value = props.reverse ? width - event.clientX + (right - width) : event.clientX - left;

      progress.value = Math.round((value / width) * unref(max));
    }

    useRender(() =>
      h(
        props.tag,
        {
          ref: intersectionRef,
          role: 'progressbar',
          class: classNames('cds-progress', unref(themeClasses), {
            'cds-progress--absolute': props.absolute,
            'cds-progress--active': props.active && unref(isIntersection),
            'cds-progress-reverse': props.reverse
          }),
          style: {
            bottom: props.location === 'bottom' ? 0 : undefined,
            top: props.location === 'top' ? 0 : undefined,
            height: props.active ? convertToUnit(unref(height)) : 0,
            '--cds-progress-height': convertToUnit(unref(height)),
            ...unref(locationStyles)
          },
          'aria-hidden': props.active ? 'false' : 'true',
          'aria-valuemin': 0,
          'aira-valuemax': props.max,
          'aria-valuenow': props.indeterminate ? undefined : unref(normalizedValue),
          onClick: props.clickable && handleClick
        },
        [
          props.stream &&
            h('div', {
              key: 'stream',
              class: classNames('cds-progress__stream'),
              style: {
                [props.reverse ? 'left' : 'right']: convertToUnit(-unref(height)),
                borderTop: `${convertToUnit(unref(height) / 2)} dotted`,
                opacity: unref(opacity),
                top: `calc(50%  - ${convertToUnit(unref(height) / 4)})`,
                width: convertToUnit(100 - unref(normalizedBuffer), '%'),
                '--cds-progress-stream-to': convertToUnit(unref(height) * (props.reverse ? 1 : -1))
              }
            }),
          h('div', {
            class: classNames('cds-progress__background'),
            style: {
              backgroundColor: props.bgOpacity,
              opacity: unref(opacity),
              width: convertToUnit(!props.stream ? 100 : unref(normalizedValue), '%')
            }
          }),
          h(
            Transition,
            {
              name: unref(transition)
            },
            {
              default: () =>
                !props.indeterminate
                  ? h('div', {
                      class: classNames('cds-progress__determinate'),
                      style: {
                        backgroundColor: props.bgColor,
                        width: convertToUnit(unref(normalizedValue), '%')
                      }
                    })
                  : h(
                      'div',
                      {
                        class: classNames('cds-progress__indeterminate')
                      },
                      ['long', 'short'].map((bar) =>
                        h('div', {
                          key: 'bar',
                          class: classNames('cds-progress__indeterminate', bar),
                          style: {
                            backgroundColor: props.bgColor
                          }
                        })
                      )
                    )
            }
          ),
          slots.default &&
            h(
              'div',
              { class: 'cds-progress__content' },
              slots.default({ value: unref(normalizedValue), buffer: unref(normalizedBuffer) })
            )
        ]
      )
    );

    return {};
  }
});
</script>

<template>Component: CdsProgress</template>

<style lang="scss">
@import './CdsProgress';
</style>
